import type { AppDispatch } from "~/store";
import type { FernieAction } from "~/store/fernieActionSlice";

import { useEffect, useRef } from "react";
import { type NavigateFunction, useLocation, useNavigate } from "react-router-dom";

import { planReportNavigation, REPORTS_VIEWPORT_MESSAGE } from "~/helpers/aiChat/reportOpenResolution";
import { useAppDispatch, useAppSelector } from "~/hooks/reduxHooks";
import { useSnacks } from "~/providers/SnackProvider";
import { dequeueAction, recordExecutedAction } from "~/store/fernieActionSlice";
import { closePanel, openPanel } from "~/store/layoutSlice";

const handleNavigate = (navigate: NavigateFunction, action: FernieAction & { type: "navigateTo" }) => {
	navigate(action.path);
};

const tryExecute = (navigate: NavigateFunction, dispatch: AppDispatch, action: FernieAction): boolean => {
	switch (action.type) {
		case "navigateTo":
			handleNavigate(navigate, action);
			return true;
		case "openPanel":
			dispatch(openPanel());
			return true;
		case "closePanel":
			dispatch(closePanel());
			return true;
		default:
			return false;
	}
};

const useFernieNavigationHandler = (): void => {
	const headAction = useAppSelector((state) => state.fernieAction.pendingActions[0]);
	const isWideScreen = useAppSelector((state) => state.layout.isWideScreen);
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const dispatch = useAppDispatch();
	const [openSnack] = useSnacks();
	const openSnackRef = useRef(openSnack);
	openSnackRef.current = openSnack;

	useEffect(() => {
		if (!headAction) return;
		if (headAction.type === "openSchedulePanel") {
			if (pathname !== headAction.path) navigate(headAction.path);
			return;
		}
		if (headAction.type === "openReport") {
			const step = planReportNavigation(pathname, headAction.path, isWideScreen);
			if (step === "navigateToReports") {
				navigate(headAction.path);
				return;
			}
			if (step === "failViewport") {
				openSnackRef.current(REPORTS_VIEWPORT_MESSAGE, "warning");
				dispatch(dequeueAction());
			}
			return;
		}
		if (tryExecute(navigate, dispatch, headAction)) {
			dispatch(recordExecutedAction(headAction));
			dispatch(dequeueAction());
		}
	}, [headAction, isWideScreen, pathname]);
};

export default useFernieNavigationHandler;
